/**
 * RoomBar Component for Poker Reborn
 *
 * Top navigation bar in the table room with:
 * - Leave Table button (left)
 * - Sound toggle button (right)
 * - Sit Out / Back button (right, when seated)
 *
 * Transparent background — sits over the table.
 *
 * @see docs/screens/room/ROOM_BAR.md - Visual specs, dimensions, behavior
 */

import { Container, Graphics, Text } from 'pixi.js';
import { Button, DANGER_BUTTON_STYLE } from '@/components/common/Button';
import { audioService } from '@/services/AudioService';
import { storageService } from '@/services/StorageService';

// =============================================================================
// STYLES
// =============================================================================

const STYLES = {
  /** Bar dimensions */
  bar: {
    height: 48,
    heightMobile: 44,
    backgroundColor: 0x000000,
    backgroundAlpha: 0.6,
    borderColor: 0xffffff,
    borderAlpha: 0.1,
    borderWidth: 1,
    padding: 12,
    zIndex: 100,
  },

  /** Button dimensions */
  button: {
    width: 80,
    widthLeave: 110,
    widthSitOut: 100,
    widthSitOutMobile: 36,
    widthSound: 100,
    widthSoundMobile: 36,
    height: 36,
    fontSize: 14,
    gap: 8, // Gap between buttons
  },

  /** Status label (below bar, left-aligned) */
  statusLabel: {
    fontSize: 10,
    fontFamily: 'Roboto, Arial, sans-serif',
    textColor: 0x888888,
    textAlpha: 0.5, // 50% transparent
    offsetY: 4, // Gap below bar
    paddingLeft: 12, // Left padding, matches bar padding
  },

  /** Responsive breakpoints */
  breakpoints: {
    mobile: 480,
    tablet: 768,
  },
};

// =============================================================================
// ROOMBAR COMPONENT
// =============================================================================

export class RoomBar extends Container {
  private screenWidth: number;
  private screenHeight: number;

  // Components
  private background: Graphics;
  private soundButton: Button;
  private sitOutButton: Button;
  private leaveButton: Button;
  private statusLabel: Text;

  // State
  private isSeated = false;
  private isSitOut = false;

  // Callbacks
  public onLeave?: () => void;
  public onSitOut?: () => void;
  public onBack?: () => void;

  constructor(width: number, height: number) {
    super();
    this.screenWidth = width;
    this.screenHeight = height;

    // Create background
    this.background = new Graphics();
    this.addChild(this.background);

    // Create sound toggle button
    const isMuted = audioService.isMuted();
    this.soundButton = new Button({
      label: isMuted ? 'Sound Off' : 'Sound On',
      width: STYLES.button.widthSound,
      height: STYLES.button.height,
      style: {
        fontSize: STYLES.button.fontSize,
      },
    });
    this.soundButton.onClick = () => { this.handleSoundToggle(); };
    this.addChild(this.soundButton);

    // Create sit out / back button (only visible when seated)
    this.sitOutButton = new Button({
      label: 'Sit Out',
      width: STYLES.button.widthSitOut,
      height: STYLES.button.height,
      style: {
        fontSize: STYLES.button.fontSize,
      },
    });
    this.sitOutButton.onClick = () => { this.handleSitOutClick(); };
    this.sitOutButton.visible = false; // Hidden until seated
    this.addChild(this.sitOutButton);

    // Create leave button
    this.leaveButton = new Button({
      label: 'Leave Table',
      width: STYLES.button.widthLeave,
      height: STYLES.button.height,
      style: {
        ...DANGER_BUTTON_STYLE,
        fontSize: STYLES.button.fontSize,
      },
    });
    this.leaveButton.onClick = () => this.onLeave?.();
    this.addChild(this.leaveButton);

    // Create status label (below bar, left-aligned, 50% transparent)
    this.statusLabel = new Text({
      text: '',
      style: {
        fill: STYLES.statusLabel.textColor,
        fontSize: STYLES.statusLabel.fontSize,
        fontFamily: STYLES.statusLabel.fontFamily,
      },
    });
    this.statusLabel.anchor.set(0, 0); // Left-aligned
    this.statusLabel.alpha = STYLES.statusLabel.textAlpha;
    this.addChild(this.statusLabel);

    // Subscribe to mute state changes
    audioService.on('muteChange', ({ muted }) => {
      this.updateSoundButton(muted);
    });

    // Initial layout
    this.layout();
  }

  /**
   * Resize the room bar
   */
  resize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;
    this.layout();
  }

  /**
   * Set status label text (grey label below bar, left-aligned)
   */
  setStatusLabel(text: string): void {
    this.statusLabel.text = text;
  }

  /**
   * Get current status label text
   */
  getStatusLabel(): string {
    return this.statusLabel.text;
  }

  /**
   * Set whether the player is seated (shows/hides sit out button)
   */
  setSeated(seated: boolean): void {
    this.isSeated = seated;
    this.sitOutButton.visible = seated;
    this.layout();
  }

  /**
   * Set whether the player is sitting out (changes button label)
   */
  setSitOut(sitOut: boolean): void {
    this.isSitOut = sitOut;
    this.updateSitOutButton();
  }

  /**
   * Clean up
   */
  destroy(): void {
    super.destroy();
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private layout(): void {
    const isMobile = this.screenWidth < STYLES.breakpoints.mobile;
    const barHeight = isMobile ? STYLES.bar.heightMobile : STYLES.bar.height;

    // Background is fully transparent
    this.background.clear();

    const buttonY = (barHeight - STYLES.button.height) / 2;

    // Update button sizes for mobile/desktop
    const isMuted = audioService.isMuted();
    if (isMobile) {
      this.soundButton.label = isMuted ? '🔇' : '🔊';
      this.soundButton.resize(STYLES.button.widthSoundMobile, STYLES.button.height);
      this.sitOutButton.label = this.isSitOut ? '↩' : '⏸';
      this.sitOutButton.resize(STYLES.button.widthSitOutMobile, STYLES.button.height);
      this.leaveButton.label = 'X';
      this.leaveButton.resize(STYLES.button.height, STYLES.button.height);
    } else {
      this.soundButton.label = isMuted ? 'Sound Off' : 'Sound On';
      this.soundButton.resize(STYLES.button.widthSound, STYLES.button.height);
      this.sitOutButton.label = this.isSitOut ? 'Back' : 'Sit Out';
      this.sitOutButton.resize(STYLES.button.widthSitOut, STYLES.button.height);
      this.leaveButton.label = 'Leave Table';
      this.leaveButton.resize(STYLES.button.widthLeave, STYLES.button.height);
    }

    // Position leave button (left side)
    this.leaveButton.x = STYLES.bar.padding;
    this.leaveButton.y = buttonY;

    // Position buttons from the right edge
    const sitOutButtonWidth = isMobile ? STYLES.button.widthSitOutMobile : STYLES.button.widthSitOut;
    const soundButtonWidth = isMobile ? STYLES.button.widthSoundMobile : STYLES.button.widthSound;

    // Sound button (rightmost)
    this.soundButton.x = this.screenWidth - soundButtonWidth - STYLES.bar.padding;
    this.soundButton.y = buttonY;

    // Sit out button (to the left of sound button)
    this.sitOutButton.x = this.soundButton.x - sitOutButtonWidth - STYLES.button.gap;
    this.sitOutButton.y = buttonY;

    // Position status label (below bar, left-aligned)
    this.statusLabel.x = STYLES.statusLabel.paddingLeft;
    this.statusLabel.y = barHeight + STYLES.statusLabel.offsetY;
  }

  private handleSoundToggle(): void {
    const newMuted = audioService.toggleMute();
    storageService.setSoundSettings({ muted: newMuted });
    this.updateSoundButton(newMuted);
  }

  private updateSoundButton(muted: boolean): void {
    const isMobile = this.screenWidth < STYLES.breakpoints.mobile;
    if (isMobile) {
      this.soundButton.label = muted ? '🔇' : '🔊';
    } else {
      this.soundButton.label = muted ? 'Sound Off' : 'Sound On';
    }
  }

  private handleSitOutClick(): void {
    if (this.isSitOut) {
      this.onBack?.();
    } else {
      this.onSitOut?.();
    }
  }

  private updateSitOutButton(): void {
    const isMobile = this.screenWidth < STYLES.breakpoints.mobile;
    if (isMobile) {
      this.sitOutButton.label = this.isSitOut ? '↩' : '⏸';
    } else {
      this.sitOutButton.label = this.isSitOut ? 'Back' : 'Sit Out';
    }
  }
}
